import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, ArrowDown, CheckCircle } from '@phosphor-icons/react';
import { format } from 'date-fns';

const CURRENCY = '\u20B9';

const AGREEMENT_BODY = `MULTILATERAL DEBT NOVATION AND SET-OFF AGREEMENT

This Multilateral Debt Novation and Set-Off Agreement (the "Agreement") is an electronic contract executed in terms of the Information Technology Act, 2000, and is entered into electronically by and between you (the "User", "You", or "Participant") and Diametor Platform (the "Platform"), along with other verified participants of the Platform who are systematically identified as part of Your specific debt-clearing cycle (the "Counterparties").

By clicking "I ACCEPT & AUTHORIZE NETTING", you unconditionally agree to the following terms:

1. Acknowledgment of Core Liabilities
You hereby explicitly declare, verify, and confirm the accuracy of the ledger balances uploaded by you onto the Platform, specifically identifying your respective Debtors (those who owe you) and Creditors (those whom you owe) (collectively, the "Original Obligations"). You warrant that these debts are legally enforceable, undisputed, and currently due.

2. Consent to Multilateral Novation (Section 62, Indian Contract Act)
The Netting Mechanism: You grant the Platform's automated routing engine the absolute authority to place your Original Obligations into an optimization matrix to detect closed loop cyclic paths involving You and multiple third-party Counterparties.
The Substitution: You explicitly agree that immediately upon the Platform's engine successfully mapping and locking a closed debt cycle, the Original Obligations between you and your immediate Creditor/Debtor shall stand novated, extinguished, and discharged up to the maximum common overlapping net amount calculated by the engine.
The New Obligation: In substitution of the extinguished Original Obligations, you agree to bound yourself strictly to the Net Obligation calculated by the Platform. If your net liability within a closed cycle is zero (INR 0), your original debt is completely discharged on paper without any physical movement of capital.

3. Platform Service Fee & Lawful Consideration
In consideration for the algorithmic netting, software routing, and structural optimization services provided by the Platform to achieve debt minimization, you agree to pay a non-refundable Technical Optimization Fee of 10% of the gross debt volume cleared.
You authorize the Platform to auto-debit or trigger a payment collection mechanism for this fee immediately upon the execution of a successful cycle path.
You acknowledge that this fee constitutes valid, legal, and sufficient consideration under Indian law to render this multilateral netting contract fully enforceable.

4. Mutual Release and Discharge
Upon successful completion of a cycle execution by the engine and payment of the Platform fee:
You completely release, acquit, and forever discharge your immediate Counterparty from their original liability to the extent of the cleared amount.
You shall have no further recourse, claims, or legal demands against the original Counterparty for the portion of the debt that has been structurally novated and cleared by the system.

5. Electronic Execution and Consent Logging
You agree that clicking the confirmation button acts as your electronic signature. You explicitly consent to the Platform maintaining an unalterable digital audit log, timestamp, and transaction hash of this network clearance, which shall serve as conclusive legal proof of debt extinguishment under Section 65B of the Indian Evidence Act.`;

export default function NovationAgreement({ open, onClose, method, feeInfo, onAuthorize, submitting }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (open) { setScrolledToBottom(false); setChecked(false); }
  }, [open]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToBottom(true);
    }
  };

  const canAuthorize = scrolledToBottom && checked && !submitting;
  const amount = method === 'now' ? feeInfo?.pay_now_amount : feeInfo?.platform_fee;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            className={`relative w-full max-w-2xl rounded-md border flex flex-col max-h-[92vh] sm:max-h-[88vh] ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}
            data-testid="novation-agreement-modal">
            {/* Header */}
            <div className={`p-5 border-b flex items-start justify-between ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              <div className="flex items-center gap-2">
                <ShieldCheck size={22} weight="bold" className="text-yellow-400" />
                <div>
                  <h3 className={`font-outfit text-base font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Legal Authorization Required</h3>
                  <p className={`font-ibm text-[11px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Signed on: {format(new Date(), 'dd MMM yyyy, HH:mm')}</p>
                </div>
              </div>
              <button onClick={onClose} className={`p-1 rounded-sm ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`} data-testid="novation-close"><X size={18} weight="bold" /></button>
            </div>

            {/* Scrollable agreement */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className={`flex-1 overflow-y-auto px-5 py-4 ${isDark ? 'bg-zinc-900/40' : 'bg-zinc-50'}`}
              style={{ scrollbarWidth: 'thin' }}
              data-testid="novation-scroll"
            >
              <pre className={`font-ibm text-[12px] leading-relaxed whitespace-pre-wrap ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{AGREEMENT_BODY}</pre>
              <div className={`mt-4 p-3 rounded-sm border ${isDark ? 'border-yellow-400/20 bg-yellow-400/5' : 'border-yellow-200 bg-yellow-50'}`}>
                <p className={`font-ibm text-xs ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Platform Fee payable {method === 'now' ? 'now (12% discount applied)' : 'via mandate on execution'}: <span className="font-bold">{CURRENCY}{(amount || 0).toLocaleString()}</span>
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className={`p-5 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
              {!scrolledToBottom && (
                <div className="flex items-center justify-center gap-2 mb-3" data-testid="scroll-hint">
                  <ArrowDown size={14} weight="bold" className="text-yellow-400 animate-bounce" />
                  <span className={`font-ibm text-[11px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Scroll to the bottom to continue</span>
                </div>
              )}
              <label className={`flex items-start gap-2 mb-4 cursor-pointer ${!scrolledToBottom ? 'opacity-40 pointer-events-none' : ''}`} data-testid="novation-checkbox-label">
                <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="mt-0.5 accent-yellow-400 w-4 h-4" data-testid="novation-checkbox" disabled={!scrolledToBottom} />
                <span className={`font-ibm text-xs ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>I sign this digital novation and authorize netting.</span>
              </label>
              <button
                onClick={() => onAuthorize(method)}
                disabled={!canAuthorize}
                className={`w-full h-12 font-outfit text-base font-bold rounded-sm transition-all flex items-center justify-center gap-2 ${canAuthorize ? 'bg-yellow-400 text-zinc-950 hover:bg-yellow-500 active:scale-[0.98]' : `${isDark ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-200 text-zinc-400'} cursor-not-allowed`}`}
                data-testid="authorize-cleardown-btn"
              >
                {submitting ? <div className="animate-spin w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full" /> : <><CheckCircle size={18} weight="bold" /> Authorize Diametor To Cleardown</>}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
